import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Card, EmptyState, LinkButton, Modal, ModalFooter, PageHeader, Row, TextAreaField, useToast } from "@plataforma/design-system";
import { formatCpf, type Appointment } from "@plataforma/shared";
import { useAppConfig } from "../../app/config";
import { api, errorMessage } from "../../lib/api";
import { initials, whatsappUrl } from "../../lib/format";
import { appointmentStatusLabel, appointmentStatusTone } from "../../lib/labels";
import { useAddNote, useClient } from "./api";
import { ClientForm } from "./ClientForm";

export function ClientProfilePage() {
  const { id } = useParams();
  const { config, format, has } = useAppConfig();
  const navigate = useNavigate();
  const profile = useClient(id);
  const history = useQuery({ queryKey: ["appointments", "client", id], enabled: has("agenda") && Boolean(id), queryFn: async () => (await api.get<{ appointments: Appointment[] }>(`/appointments?clientId=${id}`)).appointments });
  const [editing, setEditing] = useState(false);
  const [addingNote, setAddingNote] = useState(false);

  if (profile.isPending) return <EmptyState>Carregando…</EmptyState>;
  if (profile.isError || !profile.data) return <EmptyState title="Cadastro não encontrado"><Button onClick={() => navigate("/clientes")}>← Voltar</Button></EmptyState>;
  const { client, notes } = profile.data;
  const appointments = [...(history.data ?? [])].reverse();

  return (
    <>
      <PageHeader title={`Perfil do ${config.settings.terms.client.singular.toLowerCase()}`} actions={<Button onClick={() => navigate("/clientes")}>← {config.settings.terms.client.plural}</Button>} />
      <div className="client-profile">
        <Card padded>
          <div className="client-profile__title">
            <div className="client-profile__avatar" aria-hidden="true">{initials(client.name)}</div>
            <div>
              <h2 className="ds-h2">{client.name}</h2>
              <p className="ds-muted ds-small">{client.phone} · {client.email || "e-mail não informado"} · {client.cpf ? `CPF ${formatCpf(client.cpf)}` : "CPF não informado"}</p>
            </div>
          </div>
          <Row style={{ margin: "14px 0 18px" }}>
            <LinkButton size="small" href={whatsappUrl(client.phone)} target="_blank" rel="noopener">Abrir WhatsApp</LinkButton>
            <Button size="small" onClick={() => setEditing(true)}>Editar cadastro</Button>
          </Row>
          {has("agenda") ? (
            <>
              <Row align="between" style={{ marginBottom: 8 }}>
                <h3 className="ds-h3">Histórico de consultas</h3>
                <Button size="small" onClick={() => navigate(`/agenda?novo=${client.id}`)}>＋ Marcar consulta</Button>
              </Row>
              {appointments.length === 0 ? <EmptyState>Nenhuma consulta registrada.</EmptyState> : appointments.map((item) => (
                <div key={item.id} className="client-profile__history">
                  <div className="ds-muted ds-small">{format.date(item.date)}<br />{item.time}</div>
                  <div><strong className="ds-small">{item.type}</strong><div className="ds-muted ds-small">{item.locationName} · {item.durationMinutes} min · {format.money(item.value)}</div></div>
                  <Badge tone={appointmentStatusTone[item.status]}>{appointmentStatusLabel[item.status]}</Badge>
                </div>
              ))}
            </>
          ) : null}
        </Card>
        <Card padded>
          <Row align="between" style={{ marginBottom: 6 }}>
            <h3 className="ds-h3">Notas administrativas</h3>
            <Button size="small" onClick={() => setAddingNote(true)}>＋ Nota</Button>
          </Row>
          <p className="ds-muted ds-small">Preferências de contato e de agenda. Sem informações clínicas.</p>
          {notes.length === 0 ? <p className="ds-muted ds-small" style={{ marginTop: 14 }}>Nenhuma nota.</p> : notes.map((note) => (
            <div key={note.id} className="client-profile__note" style={{ marginTop: 12 }}>
              <div className="ds-muted ds-small">{format.date(note.createdAt.slice(0, 10))}{note.authorName ? ` · ${note.authorName}` : ""}</div>
              <p className="ds-small" style={{ marginTop: 4 }}>{note.text}</p>
            </div>
          ))}
        </Card>
      </div>
      {editing ? <ClientForm client={client} onClose={() => setEditing(false)} /> : null}
      {addingNote ? <NoteForm clientId={client.id} onClose={() => setAddingNote(false)} /> : null}
    </>
  );
}

function NoteForm({ clientId, onClose }: { clientId: string; onClose: () => void }) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const addNote = useAddNote(clientId);
  const toast = useToast();
  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      await addNote.mutateAsync(text);
      toast("Nota salva.");
      onClose();
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }
  return (
    <Modal title="Nota administrativa" onClose={onClose} dirty={text.length > 0}>
      <form onSubmit={submit}>
        <TextAreaField label="Nota" required maxLength={500} placeholder="Ex.: prefere contato no período da tarde" value={text} onChange={(event) => setText(event.target.value)} error={error}
          hint="Só preferências de contato e agenda. Sintomas, diagnósticos, exames e condutas ficam no prontuário." />
        <ModalFooter>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={addNote.isPending}>Salvar nota</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
